'use client';
import { ReactNode } from 'react';
import cn from 'classnames';
import { Swiper, SwiperSlide, SwiperRef } from 'swiper/react';
import type { Swiper as SwiperInstance } from 'swiper';
import { Autoplay, FreeMode, Navigation, Pagination, Virtual, EffectFade } from 'swiper/modules';
import styles from './carousel2.module.sass';
import React from 'react';
import { FontIcon } from '@intigral/shared';
import { Top1, Top2, Top3, Top4, Top5, Top6, Top7, Top8, Top9, Top10 } from '@intigral/shared';
import 'swiper/css/effect-fade'; // fade effect styles

type Slider = {
    swiperSettings?: Record<string, any>;
    children?: ReactNode | ReactNode[];
    dir?: string;
    isLoading?: boolean;
    top10?: boolean;
    reachEnd?: (swiper: SwiperInstance) => void;
    immediatePauseOnHover?: boolean;
};

const TopIcons = [Top1, Top2, Top3, Top4, Top5, Top6, Top7, Top8, Top9, Top10];

export const Carousel2 = React.forwardRef(
    (
        { swiperSettings, children, dir, isLoading, top10, reachEnd, immediatePauseOnHover, ...props }: Slider,
        ref: React.Ref<SwiperRef>
    ) => {
        const defaultSettings = {
            modules: [Autoplay, FreeMode, Navigation, Pagination, Virtual, EffectFade],
            navigation: {
                nextEl: '.swb-next',
                prevEl: '.swb-prev',
            },
            freeMode: true,
            loop: false,
            speed: 500,
            slideActiveClass: 'reset-swiper-class',
            slideNextClass: 'reset-swiper-class',
            loopAddBlankSlides: false,
            touchStartPreventDefault: false,
            slidesPerGroup: 2,
            slidesPerView: 3,
            breakpoints: {
                560: {
                    slidesPerView: 3,
                },
                768: {
                    slidesPerView: 4,
                    slidesPerGroup: 4,
                },
                924: {
                    slidesPerView: 4,
                    slidesPerGroup: 4,
                },
                1212: {
                    slidesPerView: 6,
                    slidesPerGroup: 4,
                },
                1500: {
                    slidesPerView: 7,
                    slidesPerGroup: 4,
                },
                1620: {
                    slidesPerView: 7,
                    slidesPerGroup: 4,
                },
            },
            // virtual: {
            //     addSlidesBefore: 6,
            //     addSlidesAfter: 6,
            // },
            ...swiperSettings,
            ...(immediatePauseOnHover && {
                onInit: (swiper: any) => {
                    const el = swiper.el as HTMLElement;
                    const baseSpeed = swiper.params?.speed ?? 400;

                    // keep track of paused translate and original autoplay delay
                    let pausedTranslate: number | null = null;
                    const originalDelay = swiper.params?.autoplay?.delay ?? 0;

                    const stopNow = () => {
                        // Prefer pause() if available (keeps internal state), else stop()
                        if (swiper.autoplay && typeof swiper.autoplay.pause === 'function') {
                            swiper.autoplay.pause();
                        } else {
                            swiper.autoplay?.stop();
                        }

                        // Capture current translate and lock immediately
                        const cur = swiper.getTranslate ? swiper.getTranslate() : 0;
                        pausedTranslate = cur;

                        if (swiper.setTransition) swiper.setTransition(0);
                        if (swiper.setTranslate) swiper.setTranslate(cur);

                        if (swiper.wrapperEl) swiper.wrapperEl.style.transitionDuration = '0ms';

                        // Prevent slide snapping while hovered
                        swiper.allowSlideNext = false;
                        swiper.allowSlidePrev = false;

                        (swiper as any).animating = false;
                    };

                    const startNow = () => {
                        // Re-enable slide movement
                        swiper.allowSlideNext = true;
                        swiper.allowSlidePrev = true;

                        // Only loop-fix if outside bounds (normalize bounds order)
                        const rawMin = typeof swiper.minTranslate === 'function' ? swiper.minTranslate() : -Infinity;
                        const rawMax = typeof swiper.maxTranslate === 'function' ? swiper.maxTranslate() : Infinity;
                        const beforeFix = swiper.getTranslate ? swiper.getTranslate() : 0;
                        const low = Math.min(rawMin, rawMax);
                        const high = Math.max(rawMin, rawMax);
                        const needsFix = beforeFix < low - 2 || beforeFix > high + 2; // tolerance

                        if (needsFix && typeof swiper.loopFix === 'function') {
                            swiper.loopFix();
                        }

                        // Restore the exact paused translate with no transition first to avoid a jump
                        if (swiper.setTransition) swiper.setTransition(0);
                        if (pausedTranslate != null && swiper.setTranslate) {
                            swiper.setTranslate(pausedTranslate);
                        }

                        // Keep Swiper's internal indices/classes in sync without moving the wrapper
                        try {
                            if (typeof (swiper as any).updateActiveIndex === 'function') {
                                try {
                                    (swiper as any).updateActiveIndex();
                                } catch {
                                    (swiper as any).updateActiveIndex(pausedTranslate);
                                }
                            }
                            if (typeof (swiper as any).updateSlidesClasses === 'function') {
                                (swiper as any).updateSlidesClasses();
                            }
                        } catch {
                            // console.log('Pause in swiper error');
                        }

                        // Force reflow so the 0ms translate takes effect before re-enabling transitions
                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                        swiper.wrapperEl && (swiper.wrapperEl as HTMLElement).offsetHeight;

                        // Restore transition for future moves
                        if (swiper.wrapperEl) swiper.wrapperEl.style.transitionDuration = '';
                        if (swiper.setTransition) swiper.setTransition(baseSpeed);

                        // Resume autoplay without an immediate snap
                        if (swiper.autoplay && typeof swiper.autoplay.resume === 'function') {
                            swiper.params.autoplay.delay = originalDelay || 0; // keep configured delay (0 allowed)
                            swiper.autoplay.resume();
                        } else {
                            if (swiper.params.autoplay)
                                swiper.params.autoplay.delay = Math.max(100, originalDelay || 0);
                            swiper.params.speed = baseSpeed;
                            swiper.autoplay?.start();
                            // Restore original delay on next tick
                            setTimeout(() => {
                                if (swiper?.params?.autoplay) swiper.params.autoplay.delay = originalDelay || 0;
                            }, 0);
                        }

                        // Clear paused translate marker
                        pausedTranslate = null;
                    };

                    el.addEventListener('mouseenter', stopNow);
                    el.addEventListener('mouseleave', startNow);

                    swiper.once('destroy', () => {
                        el.removeEventListener('mouseenter', stopNow);
                        el.removeEventListener('mouseleave', startNow);
                    });
                },
            }),
        };
        const top10Settings = {
            slidesPerView: 2,
            slidesPerGroup: 1,
            breakpoints: {
                560: {
                    slidesPerView: 2,
                    slidesPerGroup: 1,
                },
                768: {
                    slidesPerView: 3,
                    slidesPerGroup: 1,
                },
                924: {
                    slidesPerView: 3,
                    slidesPerGroup: 1,
                },
                1212: {
                    slidesPerView: 4,
                    slidesPerGroup: 1,
                },
                1500: {
                    slidesPerView: 5,
                    slidesPerGroup: 1,
                },
                1620: {
                    slidesPerView: 5,
                    slidesPerGroup: 1,
                },
            },
        };
        if (isLoading) {
            return (
                <div className={styles.holder}>
                    <Swiper
                        dir={dir}
                        className={cn(styles.swiper)}
                        wrapperClass={cn(styles['swiper-cont'])}
                        {...defaultSettings}
                        {...(top10 ? (swiperSettings = top10Settings) : null)}
                        {...props}
                        ref={ref}
                    >
                        {Array(7)
                            .fill('')
                            .map((_, index) => (
                                <SwiperSlide
                                    className={styles['swiper-slide']}
                                    key={`loading-slide-${index}`}
                                    virtualIndex={index}
                                >
                                    <div>{children && children}</div>
                                </SwiperSlide>
                            ))}

                        {swiperSettings?.navigation !== false && (
                            <>
                                <div className={cn(styles.prev, 'swb-prev')}>
                                    <FontIcon name="arrow" md="small"></FontIcon>
                                </div>
                                <div className={cn(styles.next, 'swb-next')}>
                                    <FontIcon name="arrow" md="small"></FontIcon>
                                </div>
                            </>
                        )}
                    </Swiper>
                </div>
            );
        }

        return (
            <div className={styles.holder}>
                <Swiper
                    dir={dir}
                    key={dir}
                    className={cn(styles.swiper)}
                    wrapperClass={cn(styles['swiper-cont'])}
                    {...{ ...defaultSettings, ...(top10 ? top10Settings : {}), ...swiperSettings }}
                    {...props}
                    ref={ref}
                    onReachEnd={reachEnd}
                >
                    {Array.isArray(children)
                        ? children.flat().map(
                              (item, index) =>
                                  item && (
                                      <SwiperSlide
                                          className={styles['swiper-slide']}
                                          key={`slide-${index}`}
                                          //   virtualIndex={Date.now() + Math.floor(Math.random() * 100) + index * 100}
                                      >
                                          {top10 ? (
                                              <div className={styles.top10}>
                                                  {TopIcons[index] ? (
                                                      <div className={styles.number}>
                                                          {React.createElement(TopIcons[index])}
                                                      </div>
                                                  ) : null}
                                                  {item}
                                              </div>
                                          ) : (
                                              item
                                          )}
                                      </SwiperSlide>
                                  )
                          )
                        : children && <SwiperSlide className={styles['swiper-slide']}>{children}</SwiperSlide>}

                    {/* Check if swiperSettings is defined and navigation is enabled */}
                    {swiperSettings?.navigation !== false && (
                        <>
                            <div className={cn(styles.prev, 'swb-prev')}>
                                <FontIcon name="arrow" md="small"></FontIcon>
                            </div>
                            <div className={cn(styles.next, 'swb-next')}>
                                <FontIcon name="arrow" md="small"></FontIcon>
                            </div>
                        </>
                    )}
                </Swiper>
            </div>
        );
    }
);
